import * as yup from '@if/core/yup'
import { ControlledPercentField, Help } from '@if/mui'
import { Box, Divider, Stack, Typography } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { EBannerOwnedFieldKey } from '@src/data/types/Constants'
import { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const buyDownConfig = [
  {
    fieldKey: EBannerOwnedFieldKey.MerchantFeeRate,
    formFieldName: 'bannerMerchantFeeRate' as const,
    titleKey: 'banner.buyDown.merchantFeeRate.title',
    descriptionKey: 'banner.buyDown.merchantFeeRate.description',
    rangeKey: 'banner.buyDown.merchantFeeRate.range',
  },
  {
    fieldKey: EBannerOwnedFieldKey.FlashPlanRateRebate,
    formFieldName: 'flashPlanRateRebate' as const,
    titleKey: 'banner.buyDown.flashPlanRateRebate.title',
    descriptionKey: 'banner.buyDown.flashPlanRateRebate.description',
    rangeKey: 'banner.buyDown.flashPlanRateRebate.range',
  },
  {
    fieldKey: EBannerOwnedFieldKey.LenderAdminFee,
    formFieldName: 'lenderAdminFee' as const,
    titleKey: 'banner.buyDown.lenderAdminFee.title',
    descriptionKey: 'banner.buyDown.lenderAdminFee.description',
    rangeKey: 'banner.buyDown.lenderAdminFee.range',
  },
] as const

// TODO: remove notRequired() and make fields required once the UseCustomFlashPlan feature switch is retired.
export const MerchantBuyDownSchema = yup.object().shape({
  bannerMerchantFeeRate: yup.number().min(0.005).max(0.2).notRequired(),
  flashPlanRateRebate: yup.number().min(0).notRequired(),
  lenderAdminFee: yup.number().min(0).notRequired(),
})

interface Props {
  bannerOwnedFields: string[]
}

const MerchantBuyDownPanel: FC<Props> = ({ bannerOwnedFields }) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const { control } = useFormContext<yup.InferType<typeof MerchantBuyDownSchema>>()

  if (!featureFlags?.useCustomFlashPlan) return null

  return (
    <>
      <Divider />
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('banner.buyDown.title')}
        </Typography>
        <Stack gap={1}>
          {buyDownConfig.map(({ fieldKey, formFieldName, titleKey, descriptionKey, rangeKey }) => {
            const isBannerOwned = bannerOwnedFields.includes(fieldKey)
            const tooltip = isBannerOwned ? t('merchant.bannerOwnedTooltip') : undefined
            return (
              <Stack
                key={fieldKey}
                direction="row"
                alignItems="flex-start"
                gap={2}
                sx={{ borderRadius: 1, px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ControlledPercentField
                  decimals={1}
                  name={formFieldName}
                  control={control}
                  label={t(titleKey)}
                  disabled={isBannerOwned}
                  size="small"
                  sx={{ width: 200 }}
                  helperText={t(rangeKey)}
                />
                <Box>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography>{t(titleKey)}</Typography>
                    {tooltip && <Help title={tooltip} />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t(descriptionKey)}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </Box>
    </>
  )
}

export default MerchantBuyDownPanel
